import React, { memo, useState, useCallback, useEffect } from 'react';
import testIds from '@smart-tv/e2e/shared/variables';

import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';

import Button from '@smart-tv/components/button/button';
import Typography from '@smart-tv/components/typography/typography';

import useFavoritesEdit from '@smart-tv/hooks/use-favorites-edit';
import useFavoritesLists from '@smart-tv/hooks/use-favorites-lists';

import {
  FavoritesScreen,
  SaveType
} from '@smart-tv/contexts/favorites-edit-context/favorites-edit-context';
import LoadingScreen from '@smart-tv/components/loading-screen/loading-screen';

import { NetworkErrorDialog } from '@smart-tv/components/error-dialog/network-error-dialog';
import useNetwork from '@smart-tv/hooks/use-network/use-network';
import styles from './favorites.module.scss';

enum SaveAction {
  Save = 'SAVE',
  Cancel = 'CANCEL'
}

export const FavoritesSave = () => {
  const { ref, focusKey, focusSelf } = useFocusable({ isFocusBoundary: true });

  const [loading, setLoading] = useState(false);
  const [failedNetworkAction, setFailedNetworkAction] = useState<SaveAction | null>(null);

  const { onSaveFavoritesList, onDeleteFavoritesList } = useFavoritesLists();
  const { editedFavoritesList, handleSelectScreen, saveType, setHasChanges } = useFavoritesEdit();
  const { isOffline, setIsOffline, checkAndUpdateNetwork } = useNetwork();

  const handleButtonClick = useCallback(
    async (action: SaveAction) => {
      const hasNetwork = await checkAndUpdateNetwork();
      if (!hasNetwork) {
        setFailedNetworkAction(action);
        return;
      }

      switch (action) {
        case SaveAction.Save:
          if (editedFavoritesList) {
            setLoading(true);
            onSaveFavoritesList(editedFavoritesList).then(() => {
              handleSelectScreen(FavoritesScreen.Home);
              setLoading(false);
            });
            setHasChanges(false);
          }
          break;
        case SaveAction.Cancel:
          handleSelectScreen(
            saveType === SaveType.Escape ? FavoritesScreen.Home : FavoritesScreen.Edit
          );
          setHasChanges(false);
          break;
      }
    },
    [
      checkAndUpdateNetwork,
      editedFavoritesList,
      onSaveFavoritesList,
      handleSelectScreen,
      setHasChanges,
      saveType,
      onDeleteFavoritesList
    ]
  );

  const closeNetworkErrorDialogAction = useCallback(() => {
    setIsOffline(false);
    setFailedNetworkAction(null);
  }, [setIsOffline]);

  useEffect(() => {
    focusSelf();
  }, [focusSelf]);

  if (loading) return <LoadingScreen />;

  return (
    <FocusContext.Provider value={focusKey}>
      {isOffline && failedNetworkAction && (
        <NetworkErrorDialog
          onTryAgain={() => handleButtonClick(failedNetworkAction)}
          onClose={closeNetworkErrorDialogAction}
        />
      )}
      <div
        className={styles['favorites-save-layout']}
        ref={ref}
        data-testid={testIds.favorites.save.root}
      >
        <Typography
          className={styles['favorites-save-root--title']}
          variant="h1"
          data-testid={testIds.favorites.save.title}
        >
          Vil du gemme ændringerne?
        </Typography>
        <Typography
          className={styles['favorites-save-root--name']}
          variant="h3"
          data-testid={testIds.favorites.save.listTitle}
        >
          {editedFavoritesList?.name}
        </Typography>
        <Button
          className={styles['favorites-save-root--button']}
          onClick={() => handleButtonClick(SaveAction.Save)}
          data-testid={testIds.favorites.save.save}
        >
          Gem ændringer
        </Button>
        <Button
          className={styles['favorites-save-root--button']}
          onClick={() => handleButtonClick(SaveAction.Cancel)}
          data-testid={testIds.favorites.save.cancel}
        >
          Fortryd ændringer
        </Button>
      </div>
    </FocusContext.Provider>
  );
};

export default memo(FavoritesSave);
